/**
 * BudgetSection — universal budget block for every "Create request" flow.
 *
 * Three modes:
 *   1. range  — min / max chips + custom inputs
 *   2. fixed  — single amount input
 *   3. unknown — "I want a quote", numeric inputs disabled
 *
 * Currency is auto-derived on the FE from the selected work_country
 *   (NO=NOK, SE=SEK, otherwise EUR) but the chosen currency is also
 *   echoed back in the value so the parent can persist it as-is.
 *
 * Chips per service vertical come from the backend
 * `/api/service-mvp/budget/presets` so they stay in sync.
 *
 * Usage:
 *   <BudgetSection
 *     category={formData.category}
 *     country={formData.country}
 *     value={formData.budget}
 *     onChange={(b) => setFormData({...formData, budget: b})}
 *   />
 *
 * `value` shape (and what `onChange` emits):
 *   { mode: 'range'|'fixed'|'unknown',
 *     min: number|null, max: number|null, fixed: number|null,
 *     currency: 'EUR'|'NOK'|'SEK'|...,
 *     is_flexible: boolean,
 *     notes: string|null }
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/lib/context';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, AlertTriangle, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Local copy of the country→currency map (kept in sync with
// backend/models/budget.py:COUNTRY_CURRENCY_MAP). Used as a fast-path
// before the presets endpoint replies.
const COUNTRY_CURRENCY = {
  NO: 'NOK', SE: 'SEK', DK: 'DKK', GB: 'GBP', PL: 'PLN', CZ: 'CZK',
};

const currencyForCountry = (c) => {
  if (!c) return 'EUR';
  return COUNTRY_CURRENCY[String(c).toUpperCase()] || 'EUR';
};

const DEFAULT_PRESETS = [
  { min: 100, max: 300 },
  { min: 300, max: 800 },
  { min: 800, max: 2000 },
  { min: 2000, max: 5000 },
  { min: 5000, max: null },
];

export const emptyBudget = (currency = 'EUR') => ({
  mode: 'unknown',
  min: null,
  max: null,
  fixed: null,
  currency,
  is_flexible: false,
  notes: null,
});

const formatChip = (p, currency) => {
  if (p.max == null) return `${p.min}+ ${currency}`;
  return `${p.min}–${p.max} ${currency}`;
};

const formatBudgetSummary = (b, t) => {
  if (!b || b.mode === 'unknown') return t('budget.summary.unknown');
  if (b.mode === 'fixed') return `${b.fixed} ${b.currency}`;
  if (b.min != null && b.max != null) return `${b.min}–${b.max} ${b.currency}`;
  if (b.min != null) return `${b.min}+ ${b.currency}`;
  if (b.max != null) return `≤ ${b.max} ${b.currency}`;
  return t('budget.summary.unknown');
};

export const BudgetSection = ({
  category,
  country,
  value,
  onChange,
  className = '',
  showWarning = true,
}) => {
  const { t } = useLanguage();
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const [warningKey, setWarningKey] = useState(null);

  // Auto-fetch presets whenever category or country changes.
  useEffect(() => {
    let active = true;
    const fetchPresets = async () => {
      try {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (country) params.set('country', country);
        const r = await fetch(`${API_URL}/api/service-mvp/budget/presets?${params.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!active) return;
        if (Array.isArray(j.presets) && j.presets.length) setPresets(j.presets);

        // If currency drifts (user changed work country), update value's currency.
        const nextCurrency = j.currency || currencyForCountry(country);
        if (value && value.currency !== nextCurrency) {
          onChange({ ...value, currency: nextCurrency });
        }
      } catch (_) {
        /* ignore — keep defaults */
      }
    };
    fetchPresets();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, country]);

  // Initialise value if missing.
  useEffect(() => {
    if (!value) {
      onChange(emptyBudget(currencyForCountry(country)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Soft warning on unrealistically low ranges
  useEffect(() => {
    if (!value || value.mode === 'unknown') { setWarningKey(null); return; }
    const compare = value.mode === 'fixed' ? value.fixed : (value.min ?? value.max);
    if (compare == null || compare <= 0) { setWarningKey(null); return; }
    // Convert to EUR-equivalent using a minimal FX table (UX hint only).
    const fx = { EUR: 1, NOK: 0.085, SEK: 0.087, DKK: 0.134, GBP: 1.18, PLN: 0.23, CZK: 0.04 }[value.currency] || 1;
    const eur = compare * fx;
    setWarningKey(eur < 25 ? 'tooLow' : null);
  }, [value]);

  const v = value || emptyBudget(currencyForCountry(country));
  const currency = v.currency || currencyForCountry(country);

  const isPresetActive = (p) =>
    v.mode === 'range' && Number(v.min) === Number(p.min) &&
    (p.max == null ? v.max == null : Number(v.max) === Number(p.max));

  const setMode = (mode) => onChange({ ...v, mode });
  const applyPreset = (p) => onChange({ ...v, mode: 'range', min: p.min, max: p.max });
  const setRange = (k, n) => onChange({ ...v, mode: 'range', [k]: n === '' ? null : Number(n) });
  const setFixed = (n) => onChange({ ...v, mode: 'fixed', fixed: n === '' ? null : Number(n) });

  return (
    <div data-testid="budget-section" className={`space-y-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-muted-foreground" />
        <Label className="font-medium">{t('budget.title')}</Label>
        <Badge variant="outline" className="ml-auto" data-testid="budget-currency-badge">
          {currency}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">{t('budget.helper')}</p>

      {/* Mode tabs */}
      <div className="flex gap-2 flex-wrap" role="tablist" aria-label="budget-mode">
        {[
          ['range', 'budget.mode.range'],
          ['fixed', 'budget.mode.fixed'],
          ['unknown', 'budget.mode.unknown'],
        ].map(([m, k]) => (
          <Button
            type="button"
            key={m}
            size="sm"
            variant={v.mode === m ? 'default' : 'outline'}
            onClick={() => setMode(m)}
            data-testid={`budget-mode-${m}`}
          >
            {t(k)}
          </Button>
        ))}
      </div>

      {/* RANGE mode: chips + custom inputs */}
      {v.mode === 'range' && (
        <div className="space-y-3" data-testid="budget-range-block">
          <div className="flex flex-wrap gap-2">
            {presets.map((p, i) => (
              <Button
                type="button"
                key={i}
                variant={isPresetActive(p) ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyPreset(p)}
                data-testid={`budget-chip-${i}`}
              >
                {formatChip(p, currency)}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t('budget.range.min')}</Label>
              <Input
                type="number"
                min={0}
                value={v.min ?? ''}
                onChange={(e) => setRange('min', e.target.value)}
                placeholder="0"
                data-testid="budget-input-min"
              />
            </div>
            <div>
              <Label className="text-xs">{t('budget.range.max')}</Label>
              <Input
                type="number"
                min={0}
                value={v.max ?? ''}
                onChange={(e) => setRange('max', e.target.value)}
                placeholder="0"
                data-testid="budget-input-max"
              />
            </div>
          </div>
        </div>
      )}

      {/* FIXED mode */}
      {v.mode === 'fixed' && (
        <div data-testid="budget-fixed-block">
          <Label className="text-xs">{t('budget.fixed.amount')}</Label>
          <Input
            type="number"
            min={0}
            value={v.fixed ?? ''}
            onChange={(e) => setFixed(e.target.value)}
            placeholder="0"
            data-testid="budget-input-fixed"
          />
        </div>
      )}

      {/* UNKNOWN mode */}
      {v.mode === 'unknown' && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground rounded-md border p-3 bg-muted/30"
             data-testid="budget-unknown-block">
          <HelpCircle className="h-4 w-4 mt-0.5" />
          <span>{t('budget.unknown.note')}</span>
        </div>
      )}

      {/* Flexible toggle (only in range/fixed) */}
      {v.mode !== 'unknown' && (
        <div className="flex items-center justify-between rounded-md border p-2">
          <Label className="text-xs">{t('budget.flexible.label')}</Label>
          <Switch
            checked={!!v.is_flexible}
            onCheckedChange={(c) => onChange({ ...v, is_flexible: c })}
            data-testid="budget-flexible-toggle"
          />
        </div>
      )}

      {/* Soft warning */}
      {showWarning && warningKey && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2"
             data-testid="budget-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>{t(`budget.warnings.${warningKey}`)}</span>
        </div>
      )}
    </div>
  );
};

export const BudgetSummary = ({ value, className = '' }) => {
  const { t } = useLanguage();
  return (
    <span data-testid="budget-summary" className={className}>
      {formatBudgetSummary(value, t)}
    </span>
  );
};

export default BudgetSection;
